import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {
  UIEntitiesRef,
  updateAllEntities,
  updateEntities,
  upsertEntities,
  upsertEntitiesById
} from '@ngneat/elf-entities';
import {purchaseStatementsStore} from '@store/invoices/purchaseStatements/purchaseStatements.store';
import {
  InvoiceLine,
  PurchaseStatementCreationDto,
  PurchaseStatementDetails,
  PurchaseStatementGroupedAction,
  PurchaseStatementsFilters,
  PurchaseStatementsPagination
} from '@store/invoices/purchaseStatements/purchaseStatements.model';
import {getPurchaseStatementsDataSource} from './purchaseStatements.requests';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {Pagination} from '@store/common/pagination.model';
import {InvoiceStatus} from '@store/invoices/invoices/invoices.model';
import {ExecutionLine, ExecutionLinePagination, InvoiceLineUpdateDto} from '@store/invoices/executionLines';
import APIAxios, {APIRoutes} from '@api/axios.api';
import {AxiosError, AxiosResponse} from 'axios';
import SnackError from '@utils/error.utils';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {TableFilter} from '@store/common/tableFilter.model';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';
import {purchaseStatementsQuery} from '@store/invoices/purchaseStatements/purchaseStatements.query';

export class PurchaseStatementsService {
  store = purchaseStatementsStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {
      search: '',
      filterAccountantTransferred: false,
      tableFilters: PurchaseStatementsFilters.initTableFilters()
    },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getPurchasesCSV() {
    const {
      filters: {from: fromDate, to: toDate, search, tableFilters, filterAccountantTransferred},
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETPurchasesCSV(),
      params: {
        ...requestFilters,
        generatedFrom: fromDate?.toISOString() || undefined,
        generatedTo: toDate?.toISOString() || undefined,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        hideTransferred: filterAccountantTransferred,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  }

  getPurchasesPiecesCSV() {
    const {
      filters: {from: fromDate, to: toDate, search, tableFilters, filterAccountantTransferred, status},
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETPurchasesPiecesCSV(),
      params: {
        ...requestFilters,
        generatedFrom: fromDate?.toISOString() || undefined,
        generatedTo: toDate?.toISOString() || undefined,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        hideTransferred: filterAccountantTransferred,
        status: status,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.csv');
        }),
      );
  }

  getPurchaseStatements = (): Observable<PurchaseStatementsPagination> => {
    const {
      filters: {from: fromDate, to: toDate, search, tableFilters, filterAccountantTransferred, status},
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETPurchases(),
      params: {
        ...requestFilters,
        generatedFrom: fromDate?.toISOString() || undefined,
        generatedTo: toDate?.toISOString() || undefined,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        hideTransferred: filterAccountantTransferred,
        status: status,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<PurchaseStatementsPagination>) => response.data),
        tap((purchaseStatementsPagination) => {
          const paginationData = Pagination.toPaginationData(purchaseStatementsPagination, tablePagination.page);

          this.store.update(
            upsertEntities(purchaseStatementsPagination.data),
            upsertEntitiesById(purchaseStatementsPagination.data.map((purchase) => purchase.id), {
              updater: (purchaseUi) => purchaseUi,
              creator: (purchaseId) => ({id: purchaseId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, purchaseStatementsPagination.data.map((purchase) => purchase.id)),
            getPurchaseStatementsDataSource.setSuccess(),
          );
        }),
        getPurchaseStatementsDataSource.trackRequestStatus(),
      );
  };

  getPurchaseStatementsDetails = (purchaseStatementId: string): Observable<PurchaseStatementDetails> => {
    return from(APIAxios({
      ...APIRoutes.GETPurchaseDetails(purchaseStatementId),
    })).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<PurchaseStatementDetails>) => {
        return response.data;
      })
    );
  };

  patchInvoiceLine = (purchaseStatementId: number | string, invoiceLineId: number, updatedInvoiceLine: Partial<ExecutionLine>): Observable<any> => {
    const data: InvoiceLineUpdateDto = {
      unitCostInCents: updatedInvoiceLine.puBuy,
      unitPriceInCents: updatedInvoiceLine.puSell,
      quantity: updatedInvoiceLine.quantity,
      name: updatedInvoiceLine.name,
    };
    return from(APIAxios({
      ...APIRoutes.PATCHPurchaseStatementInvoiceLine(purchaseStatementId, invoiceLineId),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  updateStatus = (purchaseStatementId: string, status: InvoiceStatus) => {
    /*return from(APIAxios({
      ...APIRoutes.PUTUpdatePurchaseStatus(purchaseId),
      params: {
        status: status,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, "error");
        }),
      );*/
    this.store.update(
      updateEntities(parseInt(purchaseStatementId), (purchase) => ({...purchase, status: status}))
    );
  };

  setSelectedPurchaseStatements = (purchaseStatementIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (purchaseUI) => ({
          ...purchaseUI,
          selected: purchaseStatementIds.includes(purchaseUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  postPurchaseStatement = (data: Partial<PurchaseStatementCreationDto>): Observable<any> => {
    return from(APIAxios({
      ...APIRoutes.POSTPurchaseStatement(),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error', undefined, {
            executionLineIds: (err.response?.data as any)?.executionLineIds
          } || []);
        }),
        map((response: AxiosResponse<PurchaseStatementDetails>) => response.data),
      );
  };

  getExecutionsLinesReadyForInvoicing = (): Observable<ExecutionLine[]> => {
    return from(APIAxios({
      ...APIRoutes.GETExecutionLinesReadyForInvoicing(),
      params: {
        disablePaginate: true,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionLinePagination>) => {
          return response.data?.data;
        }),
      );
  };

  addPurchaseStatementInvoiceLine = (purchaseStatementId: number | string, invoiceLine: InvoiceLine): Observable<PurchaseStatementDetails> => {
    return from(APIAxios({
      ...APIRoutes.PUTPurchaseStatementOrphanLine(purchaseStatementId),
      data: invoiceLine,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<PurchaseStatementDetails>) => response.data),
      );
  };

  removePurchaseStatementInvoiceLines = (purchaseStatementId: number | string, invoiceLinesIds: number[], revertLines: boolean): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.DELETEPurchaseStatementInvoiceLines(purchaseStatementId),
      data: {invoiceLinesIds, revertLines},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  deleteStatement = (statementId: string) => {
    return from(APIAxios({
      ...APIRoutes.DELETEPurchaseStatement(statementId)
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  getExecutionsLinesForIds(execLinesIds: number[]) {
    return from(APIAxios({
      ...APIRoutes.GETExecutionLines(),
      params: {
        ids: execLinesIds.join(','),
        disablePaginate: true,
        filterInvoicingRequired: false,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionLinePagination>) => response.data?.data),
      );
  }

  cancelPurchaseStatement(id: number, selectedInvoiceLines: number[], reason: string, invoiceNumber: string) {
    return from(APIAxios({
      ...APIRoutes.POSTCancelPurchaseStatement(id),
      data: {
        cancellationReason: reason,
        invoiceLineIds: selectedInvoiceLines,
        invoiceNumber: invoiceNumber,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<PurchaseStatementDetails>) => response.data),
      );
  }

  printInvoice(id: any) {
    return from(APIAxios({
      ...APIRoutes.GETPrintPurchaseStatement(id),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || `invoice-${id}.pdf`);
        }),
      );
  }

  updatePurchaseStatement(statementId: string, status?: InvoiceStatus, comment?: string) {
    return from(APIAxios({
      ...APIRoutes.PATCHPurchaseStatement(statementId),
      data: {
        status,
        comment
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<PurchaseStatementDetails>) => response.data),
      );
  }

  groupedAction(data: PurchaseStatementGroupedAction) {
    const ids = purchaseStatementsQuery.selectedPurchaseStatement;
    return from(APIAxios({
      ...APIRoutes.PATCHPurchaseStatementGroupedAction(),
      data: {
        ids: ids,
        status: data.status
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getPurchaseStatements())
      );
  }

  resetAccountingTransferDate(statementId: string) {
    return from(APIAxios({
      ...APIRoutes.PATCHPurchaseStatementResetAccountingTransferDate(statementId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<PurchaseStatementDetails>) => response.data),
      );
  }
}

export const purchaseStatementsService = new PurchaseStatementsService();
